import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The studio's inspector control kit — one Figma-grade property language shared
 * by every panel (community theme, profile identity, banner layers). Compact
 * 28px rows, collapsible sections, quiet borders; controls read as properties,
 * not as a settings form.
 */

/** The premium active treatment for mode pills / section tabs across the studio:
 *  a soft neutral gradient with an inset highlight — never a loud accent block. */
export const ACTIVE_PILL =
  'bg-gradient-to-b from-white/[0.16] to-white/[0.07] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_3px_rgba(0,0,0,0.45)]';

export function InspectorSection({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/[0.05]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 px-4 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-[0.8px] text-white/40 transition-colors hover:text-white/70"
      >
        {open ? <ChevronDown size={11} strokeWidth={2.6} /> : <ChevronRight size={11} strokeWidth={2.6} />}
        {title}
      </button>
      {open && <div className="space-y-2 px-4 pb-3.5">{children}</div>}
    </div>
  );
}

/** Label + control property row (Figma's two-column grid). */
export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[76px_minmax(0,1fr)] items-center gap-2">
      <span className="truncate text-[11px] font-semibold text-white/45">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** Full-width control block with a label above (for larger controls). */
export function Block({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <div>
      {label && <div className="mb-1.5 text-[11px] font-semibold text-white/45">{label}</div>}
      {children}
    </div>
  );
}

const FIELD =
  'h-7 w-full rounded-[6px] border border-white/[0.08] bg-white/[0.045] px-2 text-[12px] font-medium text-hi outline-none transition-colors placeholder:text-white/25 focus:border-accent/70 focus:bg-white/[0.07]';

export function TextField({
  value,
  onChange,
  placeholder,
  maxLength,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      className={FIELD}
    />
  );
}

export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div className="relative">
      <input
        type="number"
        value={Math.round(value)}
        min={min}
        max={max}
        step={step}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        className={cn(FIELD, '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none', suffix && 'pr-6')}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-white/30">
          {suffix}
        </span>
      )}
    </div>
  );
}

export function SliderField({
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  format = (v: number) => `${Math.round(v * 100)}%`,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  format?: (v: number) => string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="h-1 min-w-0 flex-1 cursor-pointer appearance-none rounded-full bg-white/15 accent-[var(--winr-accent)]"
      />
      <span className="w-9 flex-none text-right text-[10.5px] font-bold tabular-nums text-white/50">
        {format(value)}
      </span>
    </div>
  );
}

export function ColorField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-1.5">
      <label
        className="relative h-7 w-7 flex-none cursor-pointer overflow-hidden rounded-[6px] border border-white/[0.12]"
        style={{ backgroundColor: value }}
        title="Pick color"
      >
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 cursor-pointer opacity-0"
        />
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(FIELD, 'font-mono uppercase')}
        spellCheck={false}
      />
    </div>
  );
}

export function Seg<T extends string>({
  options,
  value,
  onChange,
  render,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  render?: (o: { id: T; label: string }) => ReactNode;
}) {
  return (
    <div className="flex h-7 gap-px overflow-hidden rounded-[6px] border border-white/[0.08] bg-black/30 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={cn(
            'min-w-0 flex-1 truncate rounded-[4px] px-1.5 text-[11px] font-semibold transition-colors',
            value === opt.id ? 'bg-white/[0.14] text-white' : 'text-white/45 hover:text-white/80',
          )}
        >
          {render ? render(opt) : opt.label}
        </button>
      ))}
    </div>
  );
}

export function Drop<T extends string>({
  value,
  options,
  onChange,
  render,
}: {
  value: T;
  options: { id: T; label: string }[];
  onChange: (id: T) => void;
  render?: (o: { id: T; label: string }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.id === value) ?? options[0]!;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(FIELD, 'flex items-center justify-between gap-1.5 text-left')}
      >
        <span className="truncate">{render ? render(current) : current.label}</span>
        <ChevronDown size={12} className={cn('flex-none text-white/35 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-60 overflow-y-auto rounded-[8px] border border-white/[0.1] bg-ink-800 p-1 shadow-[0_20px_50px_rgba(0,0,0,0.6)]">
          {options.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => {
                onChange(o.id);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-2 rounded-[5px] px-2 py-1.5 text-left text-[12px] transition-colors',
                o.id === value ? 'bg-accent/15 text-accent' : 'text-hi hover:bg-white/[0.06]',
              )}
            >
              <span className="truncate">{render ? render(o) : o.label}</span>
              {o.id === value && <Check size={12} className="flex-none" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CheckRow({
  label,
  checked,
  onChange,
  icon,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className="group flex h-7 w-full items-center justify-between gap-2 rounded-[6px] px-1.5 text-left transition-colors hover:bg-white/[0.04]"
    >
      <span className={cn('flex min-w-0 items-center gap-2 truncate text-[12px] font-semibold', checked ? 'text-hi' : 'text-white/45')}>
        {icon}
        {label}
      </span>
      <span
        className={cn(
          'grid h-[15px] w-[15px] flex-none place-items-center rounded-[4px] border transition-colors',
          checked ? 'border-accent bg-accent text-black' : 'border-white/20 group-hover:border-white/40',
        )}
      >
        {checked && <Check size={10} strokeWidth={3.2} />}
      </span>
    </button>
  );
}

export function Hint({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-[8px] border border-white/[0.06] bg-white/[0.025] px-3 py-2.5 text-[11.5px] leading-relaxed text-white/45">
      {children}
    </p>
  );
}
